/**
 * FLO-728 source-bound recap action handoff.
 *
 * The opaque capability stays in the URL fragment, which browsers do not send
 * in HTTP requests. We remove it before any API call, telemetry bootstrap, or
 * third-party asset can observe the page URL, then redeem it in a POST body.
 */

export const EMAIL_ACTION_FRAGMENT_KEY = 'floe-email-action';

const ACTION_TOKEN_RE = /^[A-Za-z0-9_-]{40,128}$/;
const REDEMPTION_TIMEOUT_MS = 4000;

export interface EmailActionLocation {
  hash: string;
  pathname: string;
  search: string;
}

export interface ConsumeEmailActionOptions {
  apiUrl: string;
  clientKey: string;
  visitorToken: string | null;
  location: EmailActionLocation;
  replaceUrl: (url: string) => void;
  navigate: (url: string) => void;
  fetcher?: typeof fetch;
  onFailure?: (kind: string) => void;
}

export type EmailActionConsumption = 'none' | 'handled' | 'failed';

function parseActionFragment(hash: string): {
  present: boolean;
  token: string | null;
} {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  if (!params.has(EMAIL_ACTION_FRAGMENT_KEY)) {
    return { present: false, token: null };
  }
  const token = params.get(EMAIL_ACTION_FRAGMENT_KEY)?.trim() ?? '';
  return {
    present: true,
    token: ACTION_TOKEN_RE.test(token) ? token : null,
  };
}

/** Remove and return a well-formed capability without performing I/O. */
export function extractEmailActionFragment(options: {
  location: EmailActionLocation;
  replaceUrl: (url: string) => void;
}): string | null {
  const parsed = parseActionFragment(options.location.hash);
  if (!parsed.present) return null;
  options.replaceUrl(`${options.location.pathname}${options.location.search}`);
  return parsed.token;
}

function httpsDestination(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

/**
 * Consume a Schedule capability without minting or adopting a visitor. A
 * missing visitor token is intentionally forwarded as null: the server can
 * still permit cross-device scheduling while withholding confirmation.
 */
export async function consumeEmailActionFragment(
  options: ConsumeEmailActionOptions,
): Promise<EmailActionConsumption> {
  const token = extractEmailActionFragment(options);
  if (!token) return 'none';

  return redeemEmailScheduleAction({ ...options, actionToken: token });
}

/** Redeem a token that was already removed from the address bar. */
export async function redeemEmailScheduleAction(
  options: Omit<ConsumeEmailActionOptions, 'location' | 'replaceUrl'> & {
    actionToken: string;
  },
): Promise<Exclude<EmailActionConsumption, 'none'>> {
  const token = options.actionToken;
  if (!ACTION_TOKEN_RE.test(token)) return 'failed';

  const apiUrl = options.apiUrl.replace(/\/+$/, '');
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REDEMPTION_TIMEOUT_MS);
  try {
    const response = await (options.fetcher ?? fetch)(
      `${apiUrl}/public/email-actions/schedule/redeem`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          actionToken: token,
          clientKey: options.clientKey,
          ...(options.visitorToken
            ? { visitorToken: options.visitorToken }
            : {}),
        }),
        credentials: 'omit',
        referrerPolicy: 'no-referrer',
        signal: controller.signal,
      },
    );
    if (!response.ok) {
      options.onFailure?.(`http_${response.status}`);
      return 'failed';
    }

    const result = (await response.json()) as {
      accepted?: unknown;
      destinationUrl?: unknown;
    };
    const destination =
      result.accepted === true ? httpsDestination(result.destinationUrl) : null;
    if (!destination) {
      options.onFailure?.('unavailable');
      return 'failed';
    }

    options.navigate(destination);
    return 'handled';
  } catch (error) {
    options.onFailure?.(
      error instanceof DOMException && error.name === 'AbortError'
        ? 'aborted'
        : 'network',
    );
    return 'failed';
  } finally {
    clearTimeout(timeoutId);
  }
}
